import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Card, CardContent, Typography, Chip, IconButton, Tooltip,
  LinearProgress, Snackbar, Alert, Divider, TextField, Button,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Checkbox, Dialog, DialogTitle, DialogContent, DialogActions,
  InputAdornment, Autocomplete, CircularProgress,
} from '@mui/material';
import {
  Shield, Security, PersonAdd, PersonRemove, Search,
  Refresh, CheckCircle, Cancel, AdminPanelSettings,
  Agriculture, Storefront, CreditCard, Gavel,
  Engineering, Assignment, HistoryEdu,
} from '@mui/icons-material';
import { rbacAPI, usersAPI } from 'services/api';

// ═══════════════════════════════════════════════════════════════
// HELPERS & CONSTANTS
// ═══════════════════════════════════════════════════════════════

const ROLE_META = {
  Admin:            { icon: <AdminPanelSettings />, color: '#dc2626', bg: '#fef2f2', desc: 'Full system access' },
  InsuranceOfficer: { icon: <Gavel />,              color: '#2563eb', bg: '#eff6ff', desc: 'Insurance + Claims + KYC' },
  CreditOfficer:    { icon: <CreditCard />,         color: '#7c3aed', bg: '#f5f3ff', desc: 'Credit + Escrow + Settlements' },
  OpsOfficer:       { icon: <Engineering />,        color: '#d97706', bg: '#fefce8', desc: 'Leads + Farms + Ponds + Ops' },
  Farmer:           { icon: <Agriculture />,        color: '#16a34a', bg: '#f0fdf4', desc: 'Self-service farmer' },
  Vendor:           { icon: <Storefront />,         color: '#0891b2', bg: '#ecfeff', desc: 'Marketplace vendor' },
};

const MODULE_ORDER = ['insurance', 'credit', 'system', 'operations', 'marketplace', 'farmer'];

const MODULE_LABELS = {
  insurance: 'Insurance', credit: 'Credit', system: 'System',
  operations: 'Operations', marketplace: 'Marketplace', farmer: 'Farmer',
};

const fmtAgo = (d) => {
  if (!d) return '—';
  const diff = Math.floor((Date.now() - new Date(d).getTime()) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
};

// ═══════════════════════════════════════════════════════════════
// REUSABLE SECTION
// ═══════════════════════════════════════════════════════════════

const Section = ({ icon, title, color, children, action, sx }) => (
  <Box sx={sx}>
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        {React.cloneElement(icon, { sx: { fontSize: 16, color } })}
        <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color, textTransform: 'uppercase', letterSpacing: '0.4px' }}>
          {title}
        </Typography>
      </Box>
      {action}
    </Box>
    {children}
  </Box>
);

// ═══════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════

const RBACManagementPage = () => {
  // ── State ──
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedRole, setSelectedRole] = useState(null);
  const [roleDetail, setRoleDetail] = useState(null); // full role with permissions

  // User role assignment
  const [selectedUser, setSelectedUser] = useState(null);
  const [userRoles, setUserRoles] = useState(null); // UserRolesResponse
  const [assigningRole, setAssigningRole] = useState(false);
  const [revokingRole, setRevokingRole] = useState(null);

  // Confirm dialog
  const [confirmDialog, setConfirmDialog] = useState({ open: false, title: '', message: '', onConfirm: null });

  // Audit log (client-side tracking)
  const [auditLog, setAuditLog] = useState([]);

  const addAudit = useCallback((action, detail) => {
    setAuditLog(prev => [{
      id: Date.now(),
      action,
      detail,
      timestamp: new Date().toISOString(),
      performedBy: 'Current Admin',
    }, ...prev].slice(0, 50));
  }, []);

  const toast = useCallback((message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  }, []);

  // ── Load Data ──
  const loadRoles = useCallback(async () => {
    try {
      const res = await rbacAPI.listRoles();
      setRoles(res.data || []);
    } catch (err) {
      console.error('Failed to load roles:', err);
      toast('Failed to load roles', 'error');
    }
  }, [toast]);

  const loadPermissions = useCallback(async () => {
    try {
      const res = await rbacAPI.listPermissions();
      setPermissions(res.data || []);
    } catch (err) {
      console.error('Failed to load permissions:', err);
    }
  }, []);

  const loadUsers = useCallback(async () => {
    try {
      const res = await usersAPI.getAll();
      setUsers(res.data || []);
    } catch (err) {
      console.error('Failed to load users:', err);
    }
  }, []);

  const loadAll = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true); else setLoading(true);
    await Promise.all([loadRoles(), loadPermissions(), loadUsers()]);
    setLoading(false);
    setRefreshing(false);
    if (isRefresh) toast('Data refreshed');
  }, [loadRoles, loadPermissions, loadUsers, toast]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // ── Select role and load details ──
  const handleSelectRole = useCallback(async (roleName) => {
    setSelectedRole(roleName);
    try {
      const res = await rbacAPI.getRole(roleName);
      setRoleDetail(res.data);
    } catch (err) {
      console.error('Failed to load role detail:', err);
      setRoleDetail(null);
    }
  }, []);

  // Auto-select first role
  useEffect(() => {
    if (roles.length > 0 && !selectedRole) {
      handleSelectRole(roles[0].name);
    }
  }, [roles, selectedRole, handleSelectRole]);

  // ── Lookup user roles ──
  const handleLookupUser = useCallback(async (user) => {
    if (!user) { setUserRoles(null); return; }
    setSelectedUser(user);
    try {
      const res = await rbacAPI.getUserRoles(user.id);
      setUserRoles(res.data);
    } catch (err) {
      console.error('Failed to load user roles:', err);
      toast('Failed to load user roles', 'error');
    }
  }, [toast]);

  // ── Assign role ──
  const handleAssignRole = useCallback(async (userId, roleName) => {
    setAssigningRole(true);
    try {
      await rbacAPI.assignRole(userId, roleName);
      toast(`Role "${roleName}" assigned successfully`);
      addAudit('Assign Role', `Assigned "${roleName}" to user #${userId}`);
      // Refresh user roles
      const res = await rbacAPI.getUserRoles(userId);
      setUserRoles(res.data);
      await loadRoles();
    } catch (err) {
      toast(err.response?.data?.detail || 'Failed to assign role', 'error');
    } finally {
      setAssigningRole(false);
    }
  }, [toast, addAudit, loadRoles]);

  // ── Revoke role ──
  const handleRevokeRole = useCallback(async (userId, roleName) => {
    setRevokingRole(roleName);
    try {
      await rbacAPI.revokeRole(userId, roleName);
      toast(`Role "${roleName}" revoked successfully`);
      addAudit('Revoke Role', `Revoked "${roleName}" from user #${userId}`);
      const res = await rbacAPI.getUserRoles(userId);
      setUserRoles(res.data);
      await loadRoles();
    } catch (err) {
      toast(err.response?.data?.detail || 'Failed to revoke role', 'error');
    } finally {
      setRevokingRole(null);
    }
  }, [toast, addAudit, loadRoles]);

  // ── Permission matrix data ──
  const matrixData = useMemo(() => {
    // Group non-legacy permissions by module
    const nonLegacy = permissions.filter(p =>
      p.module && MODULE_ORDER.includes(p.module) && !p.code.includes('[legacy]')
    );
    const byModule = {};
    for (const p of nonLegacy) {
      if (!byModule[p.module]) byModule[p.module] = [];
      if (!byModule[p.module].find(x => x.code === p.code)) {
        byModule[p.module].push(p);
      }
    }
    return MODULE_ORDER
      .filter(m => byModule[m]?.length > 0)
      .map(m => ({ module: m, permissions: byModule[m].sort((a, b) => a.code.localeCompare(b.code)) }));
  }, [permissions]);

  const primaryRoles = useMemo(() =>
    roles.filter(r => Object.keys(ROLE_META).includes(r.name)),
    [roles]
  );

  // ── Build role→permissionCodes lookup from detailed fetches ──
  const [rolePermMap, setRolePermMap] = useState({});
  useEffect(() => {
    if (primaryRoles.length === 0) return;
    const fetchAll = async () => {
      const map = {};
      await Promise.all(primaryRoles.map(async (r) => {
        try {
          const res = await rbacAPI.getRole(r.name);
          map[r.name] = (res.data.permissions || []).map(p => p.code);
        } catch {
          map[r.name] = [];
        }
      }));
      setRolePermMap(map);
    };
    fetchAll();
  }, [primaryRoles]);

  // ── Loading ──
  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
        <Box sx={{ width: 48, height: 48, border: '4px solid #e2e8f0', borderTop: '4px solid #0f172a', borderRadius: '50%', animation: 'spin 0.8s linear infinite', '@keyframes spin': { '0%': { transform: 'rotate(0deg)' }, '100%': { transform: 'rotate(360deg)' } } }} />
        <Typography sx={{ color: '#64748b', mt: 2, fontWeight: 500 }}>Loading RBAC configuration…</Typography>
      </Box>
    );
  }

  // ═══════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f1f5f9', p: { xs: 1.5, md: 2.5 } }}>
      {refreshing && <LinearProgress sx={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999 }} />}

      {/* ══════════ HEADER ══════════ */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: '#0f172a', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Shield sx={{ color: '#38bdf8', fontSize: 22 }} />
          </Box>
          <Box>
            <Typography sx={{ fontSize: '1.1rem', fontWeight: 800, color: '#0f172a', lineHeight: 1.2 }}>
              RBAC & Roles Management
            </Typography>
            <Typography sx={{ fontSize: '0.72rem', color: '#64748b' }}>
              Manage roles, permissions, and user assignments
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Chip
            label={`${roles.length} Roles`}
            size="small"
            sx={{ bgcolor: '#eff6ff', color: '#2563eb', fontWeight: 700, fontSize: '0.65rem' }}
          />
          <Chip
            label={`${permissions.length} Permissions`}
            size="small"
            sx={{ bgcolor: '#f5f3ff', color: '#7c3aed', fontWeight: 700, fontSize: '0.65rem' }}
          />
          <Tooltip title="Refresh">
            <IconButton size="small" onClick={() => loadAll(true)} disabled={refreshing}>
              <Refresh sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* ══════════ 2. ROLE LIST PANEL ══════════ */}
      <Section icon={<Security />} title="Role List" color="#2563eb" sx={{ mb: 3 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', md: 'repeat(6, 1fr)' }, gap: 2 }}>
          {Object.entries(ROLE_META).map(([name, meta]) => {
            const role = roles.find(r => r.name === name);
            const isSelected = selectedRole === name;
            return (
              <Card
                key={name}
                onClick={() => handleSelectRole(name)}
                sx={{
                  borderRadius: 2, cursor: 'pointer',
                  border: isSelected ? `2px solid ${meta.color}` : '1px solid #e2e8f0',
                  bgcolor: isSelected ? meta.bg : '#fff',
                  transition: 'all 0.2s ease',
                  '&:hover': { transform: 'translateY(-2px)', boxShadow: `0 4px 16px ${meta.color}20` },
                }}
              >
                <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 }, textAlign: 'center' }}>
                  <Box sx={{ width: 36, height: 36, borderRadius: 2, bgcolor: meta.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 0.5 }}>
                    {React.cloneElement(meta.icon, { sx: { fontSize: 18, color: meta.color } })}
                  </Box>
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#0f172a' }}>
                    {name}
                  </Typography>
                  <Typography sx={{ fontSize: '0.55rem', color: '#94a3b8', mb: 0.5 }}>
                    {meta.desc}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                    <Chip
                      label={role?.is_active ? 'Active' : 'Inactive'}
                      size="small"
                      sx={{
                        height: 16, fontSize: '0.48rem', fontWeight: 700,
                        bgcolor: role?.is_active ? '#dcfce7' : '#fef2f2',
                        color: role?.is_active ? '#16a34a' : '#dc2626',
                      }}
                    />
                    <Chip
                      label={`${role?.permission_count || 0} perms`}
                      size="small"
                      sx={{ height: 16, fontSize: '0.48rem', fontWeight: 600, bgcolor: '#f1f5f9', color: '#64748b' }}
                    />
                  </Box>
                </CardContent>
              </Card>
            );
          })}
        </Box>
      </Section>

      {/* ══════════ 3. PERMISSION MATRIX ══════════ */}
      <Section icon={<Assignment />} title="Permission Matrix" color="#7c3aed" sx={{ mb: 3 }}>
        <Card variant="outlined" sx={{ borderRadius: 2, borderColor: '#e2e8f0' }}>
          <TableContainer sx={{ maxHeight: 480 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ bgcolor: '#f8fafc', fontWeight: 700, fontSize: '0.62rem', color: '#64748b', py: 1, position: 'sticky', left: 0, zIndex: 3, minWidth: 200 }}>
                    Module / Permission
                  </TableCell>
                  {primaryRoles.map(r => {
                    const meta = ROLE_META[r.name] || {};
                    return (
                      <TableCell key={r.name} align="center" sx={{ bgcolor: '#f8fafc', fontWeight: 700, fontSize: '0.58rem', color: meta.color || '#64748b', py: 1, minWidth: 90 }}>
                        {r.name}
                      </TableCell>
                    );
                  })}
                </TableRow>
              </TableHead>
              <TableBody>
                {matrixData.map((group) => (
                  <React.Fragment key={group.module}>
                    {/* Module header row */}
                    <TableRow>
                      <TableCell
                        colSpan={primaryRoles.length + 1}
                        sx={{ bgcolor: '#f1f5f9', py: 0.5, position: 'sticky', left: 0 }}
                      >
                        <Typography sx={{ fontSize: '0.62rem', fontWeight: 800, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          {MODULE_LABELS[group.module] || group.module}
                        </Typography>
                      </TableCell>
                    </TableRow>
                    {group.permissions.map((perm) => (
                      <TableRow key={perm.code} sx={{ '&:hover': { bgcolor: '#fafbfc' } }}>
                        <TableCell sx={{ py: 0.4, position: 'sticky', left: 0, bgcolor: '#fff', zIndex: 1 }}>
                          <Typography sx={{ fontSize: '0.62rem', fontWeight: 600, color: '#1e293b' }}>
                            {perm.name}
                          </Typography>
                          <Typography sx={{ fontSize: '0.5rem', color: '#94a3b8', fontFamily: 'monospace' }}>
                            {perm.code}
                          </Typography>
                        </TableCell>
                        {primaryRoles.map(r => {
                          const hasPerm = (rolePermMap[r.name] || []).includes(perm.code);
                          return (
                            <TableCell key={r.name} align="center" sx={{ py: 0.4 }}>
                              <Checkbox
                                checked={hasPerm}
                                size="small"
                                disabled
                                sx={{
                                  p: 0.3,
                                  color: '#e2e8f0',
                                  '&.Mui-checked': { color: ROLE_META[r.name]?.color || '#2563eb' },
                                  '&.Mui-disabled': { color: hasPerm ? (ROLE_META[r.name]?.color || '#2563eb') : '#e2e8f0' },
                                }}
                              />
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    ))}
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {matrixData.length === 0 && (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography sx={{ color: '#94a3b8', fontSize: '0.82rem' }}>No permission data loaded yet.</Typography>
            </Box>
          )}
        </Card>
      </Section>

      {/* ══════════ TWO-COLUMN: USER ASSIGNMENT + AUDIT ══════════ */}
      <Box sx={{ display: 'flex', gap: 2.5, flexDirection: { xs: 'column', md: 'row' } }}>

        {/* ── 4. USER ROLE ASSIGNMENT ── */}
        <Box sx={{ flex: 1 }}>
          <Section icon={<PersonAdd />} title="User Role Assignment" color="#16a34a">
            <Card variant="outlined" sx={{ borderRadius: 2, borderColor: '#e2e8f0' }}>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                {/* Search users */}
                <Autocomplete
                  options={users}
                  getOptionLabel={(u) => `${u.name || u.username} (${u.email}) — ID #${u.id}`}
                  filterOptions={(options, { inputValue }) => {
                    const q = inputValue.toLowerCase();
                    if (!q) return options.slice(0, 20);
                    return options.filter(u =>
                      (u.name || '').toLowerCase().includes(q) ||
                      (u.email || '').toLowerCase().includes(q) ||
                      (u.username || '').toLowerCase().includes(q) ||
                      String(u.id).includes(q)
                    ).slice(0, 20);
                  }}
                  onChange={(_, value) => handleLookupUser(value)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      placeholder="Search by name, email, or ID…"
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <InputAdornment position="start">
                            <Search sx={{ fontSize: 16, color: '#94a3b8' }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={{ mb: 2, '& .MuiOutlinedInput-root': { fontSize: '0.78rem' } }}
                    />
                  )}
                  renderOption={(props, u) => (
                    <Box component="li" {...props} key={u.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: '0.75rem' }}>
                      <Box>
                        <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: '#1e293b' }}>
                          {u.name || u.username}
                        </Typography>
                        <Typography sx={{ fontSize: '0.58rem', color: '#94a3b8' }}>
                          {u.email} • {u.role} • ID #{u.id}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                />

                {/* User details & current roles */}
                {userRoles && (
                  <Box>
                    <Divider sx={{ mb: 1.5 }} />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                      <Box sx={{ width: 32, height: 32, borderRadius: '50%', bgcolor: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <PersonAdd sx={{ fontSize: 16, color: '#2563eb' }} />
                      </Box>
                      <Box>
                        <Typography sx={{ fontSize: '0.76rem', fontWeight: 700, color: '#0f172a' }}>
                          {userRoles.username}
                        </Typography>
                        <Typography sx={{ fontSize: '0.58rem', color: '#94a3b8' }}>
                          {userRoles.email} • Legacy: {userRoles.legacy_role}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Current RBAC roles */}
                    <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, color: '#64748b', mb: 0.5, textTransform: 'uppercase' }}>
                      Current RBAC Roles
                    </Typography>
                    {userRoles.rbac_roles?.length > 0 ? (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1.5 }}>
                        {userRoles.rbac_roles.map(r => {
                          const meta = ROLE_META[r.name] || { color: '#64748b', bg: '#f1f5f9' };
                          return (
                            <Chip
                              key={r.name}
                              label={r.name}
                              size="small"
                              onDelete={() => {
                                setConfirmDialog({
                                  open: true,
                                  title: 'Revoke Role',
                                  message: `Remove "${r.name}" from ${userRoles.username}?`,
                                  onConfirm: () => handleRevokeRole(userRoles.user_id, r.name),
                                });
                              }}
                              deleteIcon={revokingRole === r.name ? <CircularProgress size={12} /> : <Cancel sx={{ fontSize: 14 }} />}
                              sx={{
                                fontWeight: 600, fontSize: '0.6rem',
                                bgcolor: meta.bg, color: meta.color,
                                '& .MuiChip-deleteIcon': { color: meta.color, opacity: 0.6, '&:hover': { opacity: 1 } },
                              }}
                            />
                          );
                        })}
                      </Box>
                    ) : (
                      <Typography sx={{ fontSize: '0.62rem', color: '#94a3b8', mb: 1.5, fontStyle: 'italic' }}>
                        No RBAC roles assigned
                      </Typography>
                    )}

                    {/* Assign new role */}
                    <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, color: '#64748b', mb: 0.5, textTransform: 'uppercase' }}>
                      Assign Role
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {Object.entries(ROLE_META).map(([name, meta]) => {
                        const alreadyAssigned = (userRoles.rbac_roles || []).some(r => r.name === name);
                        return (
                          <Button
                            key={name}
                            size="small"
                            variant={alreadyAssigned ? 'contained' : 'outlined'}
                            disabled={alreadyAssigned || assigningRole}
                            onClick={() => {
                              setConfirmDialog({
                                open: true,
                                title: 'Assign Role',
                                message: `Assign "${name}" to ${userRoles.username}?`,
                                onConfirm: () => handleAssignRole(userRoles.user_id, name),
                              });
                            }}
                            startIcon={assigningRole ? <CircularProgress size={12} /> : React.cloneElement(meta.icon, { sx: { fontSize: 14 } })}
                            sx={{
                              fontSize: '0.58rem', fontWeight: 600, textTransform: 'none',
                              borderColor: meta.color, color: alreadyAssigned ? '#fff' : meta.color,
                              bgcolor: alreadyAssigned ? meta.color : 'transparent',
                              '&:hover': { bgcolor: alreadyAssigned ? meta.color : `${meta.color}10`, borderColor: meta.color },
                              '&.Mui-disabled': alreadyAssigned ? { bgcolor: `${meta.color}80`, color: '#fff' } : {},
                            }}
                          >
                            {name}
                          </Button>
                        );
                      })}
                    </Box>

                    {/* User permissions summary */}
                    {userRoles.permissions?.length > 0 && (
                      <Box sx={{ mt: 2 }}>
                        <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, color: '#64748b', mb: 0.5, textTransform: 'uppercase' }}>
                          Effective Permissions ({userRoles.permissions.length})
                        </Typography>
                        <Box sx={{ maxHeight: 120, overflowY: 'auto', display: 'flex', flexWrap: 'wrap', gap: 0.3 }}>
                          {userRoles.permissions.filter(p => !p.includes('[legacy]')).map(code => (
                            <Chip
                              key={code}
                              label={code}
                              size="small"
                              sx={{ height: 18, fontSize: '0.48rem', fontFamily: 'monospace', bgcolor: '#f1f5f9', color: '#475569' }}
                            />
                          ))}
                        </Box>
                      </Box>
                    )}
                  </Box>
                )}

                {!userRoles && !selectedUser && (
                  <Box sx={{ textAlign: 'center', py: 3 }}>
                    <Search sx={{ fontSize: 36, color: '#e2e8f0', mb: 0.5 }} />
                    <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8' }}>
                      Search for a user to manage their roles
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Section>
        </Box>

        {/* ── 5. ROLE CHANGE AUDIT LOG ── */}
        <Box sx={{ flex: 1 }}>
          <Section icon={<HistoryEdu />} title="Role Change Audit Log" color="#d97706">
            <Card variant="outlined" sx={{ borderRadius: 2, borderColor: '#e2e8f0' }}>
              {auditLog.length === 0 ? (
                <CardContent sx={{ p: 4, textAlign: 'center' }}>
                  <HistoryEdu sx={{ fontSize: 36, color: '#e2e8f0', mb: 0.5 }} />
                  <Typography sx={{ fontSize: '0.76rem', fontWeight: 600, color: '#94a3b8' }}>
                    No role changes in this session
                  </Typography>
                  <Typography sx={{ fontSize: '0.6rem', color: '#cbd5e1', mt: 0.5 }}>
                    Role assignments and revocations will appear here
                  </Typography>
                </CardContent>
              ) : (
                <TableContainer sx={{ maxHeight: 400 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ bgcolor: '#f8fafc', fontWeight: 700, fontSize: '0.62rem', color: '#64748b', py: 0.8 }}>Time</TableCell>
                        <TableCell sx={{ bgcolor: '#f8fafc', fontWeight: 700, fontSize: '0.62rem', color: '#64748b', py: 0.8 }}>Action</TableCell>
                        <TableCell sx={{ bgcolor: '#f8fafc', fontWeight: 700, fontSize: '0.62rem', color: '#64748b', py: 0.8 }}>Detail</TableCell>
                        <TableCell sx={{ bgcolor: '#f8fafc', fontWeight: 700, fontSize: '0.62rem', color: '#64748b', py: 0.8 }}>By</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {auditLog.map((entry) => (
                        <TableRow key={entry.id} sx={{ '&:hover': { bgcolor: '#fafbfc' } }}>
                          <TableCell sx={{ py: 0.6, fontSize: '0.6rem', fontFamily: 'monospace', color: '#64748b', whiteSpace: 'nowrap' }}>
                            {fmtAgo(entry.timestamp)}
                          </TableCell>
                          <TableCell sx={{ py: 0.6 }}>
                            <Chip
                              label={entry.action}
                              size="small"
                              icon={entry.action === 'Assign Role'
                                ? <CheckCircle sx={{ fontSize: 12 }} />
                                : <PersonRemove sx={{ fontSize: 12 }} />}
                              sx={{
                                height: 20, fontSize: '0.52rem', fontWeight: 700,
                                bgcolor: entry.action === 'Assign Role' ? '#dcfce7' : '#fef2f2',
                                color: entry.action === 'Assign Role' ? '#16a34a' : '#dc2626',
                                '& .MuiChip-icon': { color: 'inherit' },
                              }}
                            />
                          </TableCell>
                          <TableCell sx={{ py: 0.6, fontSize: '0.6rem', color: '#1e293b' }}>
                            {entry.detail}
                          </TableCell>
                          <TableCell sx={{ py: 0.6, fontSize: '0.58rem', color: '#64748b' }}>
                            {entry.performedBy}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Card>
          </Section>

          {/* Selected role details */}
          {roleDetail && (
            <Section icon={<Shield />} title={`${selectedRole} — Permissions`} color={ROLE_META[selectedRole]?.color || '#475569'} sx={{ mt: 2.5 }}>
              <Card variant="outlined" sx={{ borderRadius: 2, borderColor: '#e2e8f0' }}>
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Box sx={{
                      width: 28, height: 28, borderRadius: 1.5,
                      bgcolor: ROLE_META[selectedRole]?.bg || '#f1f5f9',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      {ROLE_META[selectedRole]?.icon
                        ? React.cloneElement(ROLE_META[selectedRole].icon, { sx: { fontSize: 14, color: ROLE_META[selectedRole].color } })
                        : <Shield sx={{ fontSize: 14, color: '#64748b' }} />}
                    </Box>
                    <Box>
                      <Typography sx={{ fontSize: '0.76rem', fontWeight: 700, color: '#0f172a' }}>
                        {roleDetail.name}
                      </Typography>
                      <Typography sx={{ fontSize: '0.55rem', color: '#94a3b8' }}>
                        {roleDetail.description} • {roleDetail.permissions?.length || 0} permissions
                      </Typography>
                    </Box>
                    <Chip
                      label={roleDetail.is_active ? 'Active' : 'Inactive'}
                      size="small"
                      sx={{
                        ml: 'auto', height: 18, fontSize: '0.5rem', fontWeight: 700,
                        bgcolor: roleDetail.is_active ? '#dcfce7' : '#fef2f2',
                        color: roleDetail.is_active ? '#16a34a' : '#dc2626',
                      }}
                    />
                  </Box>
                  <Divider sx={{ mb: 1 }} />
                  <Box sx={{ maxHeight: 200, overflowY: 'auto', display: 'flex', flexWrap: 'wrap', gap: 0.4 }}>
                    {(roleDetail.permissions || [])
                      .filter(p => !p.name?.includes('[legacy]'))
                      .sort((a, b) => (a.module || '').localeCompare(b.module || '') || a.code.localeCompare(b.code))
                      .map(p => (
                        <Chip
                          key={p.code}
                          label={`${p.module ? p.module + '.' : ''}${p.code.replace(`${p.module}.`, '')}`}
                          size="small"
                          sx={{
                            height: 20, fontSize: '0.5rem', fontFamily: 'monospace',
                            bgcolor: '#f1f5f9', color: '#475569',
                          }}
                        />
                      ))}
                  </Box>
                </CardContent>
              </Card>
            </Section>
          )}
        </Box>
      </Box>

      {/* ══════════ CONFIRM DIALOG ══════════ */}
      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ ...confirmDialog, open: false })}
        PaperProps={{ sx: { borderRadius: 2, maxWidth: 400 } }}
      >
        <DialogTitle sx={{ fontSize: '0.9rem', fontWeight: 700, pb: 0.5 }}>
          {confirmDialog.title}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: '0.78rem', color: '#475569' }}>
            {confirmDialog.message}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            size="small"
            onClick={() => setConfirmDialog({ ...confirmDialog, open: false })}
            sx={{ textTransform: 'none', fontWeight: 600, color: '#64748b' }}
          >
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={() => {
              confirmDialog.onConfirm?.();
              setConfirmDialog({ ...confirmDialog, open: false });
            }}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* ══════════ SNACKBAR ══════════ */}
      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%', borderRadius: 2 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RBACManagementPage;
